import React, { useMemo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 *
 */
interface SidebarDragCardProps {
  playerName: string;
  contractValue: string;
  contractMonths: number;
  teamName: string;
  age: number;
  archetype: string;
  gpr: number;
}

/**
 * SidebarDragCard Component
 *
 * Compact drag overlay that matches the sidebar player card layout
 * Used specifically for sidebar-to-kanban drag operations
 * @param root0 - Component props
 * @param root0.playerName - Full name of the player
 * @param root0.contractValue - Formatted contract value string with currency symbol
 * @param root0.contractMonths - Contract duration in months
 * @param root0.teamName - Name of player's current team
 * @param root0.age - Player's age in years
 * @param root0.archetype - Player archetype or position classification
 * @param root0.gpr - Gemini Performance Rating score
 */
const SidebarDragCardContainer = ({
  playerName,
  contractValue,
  contractMonths,
  teamName,
  age,
  archetype,
  gpr,
}: SidebarDragCardProps) => {
  // Use the formatted value with currency symbol from formatCurrencyShort
  const simplifiedValue = useMemo(() => {
    // contractValue already includes the proper currency symbol
    return contractValue;
  }, [contractValue]);

  const boxStyle = useMemo(
    () => ({
      backgroundColor: "rgba(139, 92, 246, 0.2)",
      borderColor: "#8B5CF6",
    }),
    []
  );

  return (
    <Box
      className="w-80 rounded-sm border-2 p-4 shadow-lg"
      // eslint-disable-next-line no-restricted-syntax -- drag-overlay accent purple bg/border are off-vocabulary literals with no semantic token
      UNSAFE_style={boxStyle}
    >
      <Stack space="2">
        <Stack direction="horizontal" className="items-start justify-between">
          <Stack className="flex-1 gap-micro">
            <Text variant="body-strong" className="text-white">
              {playerName}
            </Text>
            <Text variant="caption" className="text-content-muted">
              {archetype} • {age}y
            </Text>
            <Text variant="caption" className="text-content-muted">
              {teamName}
            </Text>
          </Stack>
          <Stack className="items-end gap-px">
            <Text variant="body-strong" className="text-white">
              {simplifiedValue}
            </Text>
            <Text variant="caption" className="text-content-muted">
              {contractMonths}mo
            </Text>
            <Text variant="caption" className="text-content-muted">
              GPR {gpr}
            </Text>
          </Stack>
        </Stack>

        {/* Divider */}
        <Box className="h-px bg-outline-subtle opacity-50" />
      </Stack>
    </Box>
  );
};

export default SidebarDragCardContainer;
